import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCreateMessage } from '@/api/mutations/useCreateMessage';
import { useCreateThread } from '@/api/mutations/useCreateThread';
import { sanitiseKimQuestion } from '@/helpers/sanitiseKimQuestion';
import { ExecuteKimSearch, KimMode } from '@/types';

interface FollowUpQuestionsProps {
  followUpQuestions?: string[];
  questionMode: KimMode;
}

export function FollowUpQuestions({ followUpQuestions, questionMode }: FollowUpQuestionsProps) {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return;

  const navigate = useNavigate();
  const { mutate: createThread, isLoading: isCreatingThread } = useCreateThread(matterId, subsetId);
  const { mutate: createMessage, isLoading: isCreatingMessage } = useCreateMessage();

  const executeSearch: ExecuteKimSearch = ({ question: value, questionMode }) => {
    const question = sanitiseKimQuestion(value);

    createThread(question, {
      onSuccess: (data) => {
        createMessage({
          matterId,
          subsetId,
          question,
          mode: questionMode,
          docIds: [],
          draftType: null,
          threadId: data.id,
        });

        trackCustomEvent(TrackingEventsV2.ThreadFollowUpQuestion);
        navigate(`/matters/${matterId}/subsets/${subsetId}/kim/${data.id}`);
      },
      onError: (error) => {
        console.error('Error creating thread and message');
        toast.error('Error processing your question. Please try again.');
      },
    });
  };

  const hasNoContent = !followUpQuestions || followUpQuestions?.length === 0;

  if (hasNoContent) {
    return null;
  }

  return (
    <>
      <div className="mx-2 mt-4 mb-2 flex items-center gap-3">
        <span className="iconify lucide--message-circle-question text-brandSecondary mb-0.5 size-5" />

        <h2 className="text-sm font-semibold text-gray-800">Follow up questions</h2>
      </div>

      <div className="flex flex-col gap-2 rounded-md p-2">
        {followUpQuestions.map((question) => (
          <div className={`rounded px-2 py-1 hover:bg-gray-100`} key={question}>
            <div>
              <button
                className="flex w-full items-center justify-between gap-4 rounded py-1 text-left text-sm"
                onClick={() => executeSearch({ question, questionMode })}
                disabled={isCreatingThread || isCreatingMessage}
              >
                <p className="text-xs">{question}</p>

                <span className="iconify lucide--arrow-right text-brandSecondary h-4 w-4 shrink-0" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
